'use client'

import { useCallback, useEffect, useState } from 'react'
import screens, { ScreenNames, screenNames } from '../settings/screens'

/**
 * Generates a MediaQueryList object matching the provided screen name
 * @param screenName Screen name based on settings/screens.ts
 */
export function useScreen(screenName: ScreenNames) {
  const [matchMedia, setMatchMedia] = useState<MediaQueryList | undefined>()

  const matchMediaFn = useCallback(() => {
    if (screenNames.includes(screenName)) {
      // Get media settings for the matching screen
      const { min, max } = screens[screenName]

      // Build out media query
      const query: string[] = []

      if (min) {
        query.push(`(min-width: ${min}px)`)
      }

      if (max) {
        query.push(`(max-width: ${max}px)`)
      }

      setMatchMedia(window.matchMedia(query.join(' and ')))
    }
  }, [screenName])

  useEffect(() => {
    matchMediaFn()
  }, [matchMediaFn, screenName])

  useEffect(() => {
    matchMedia?.addEventListener('change', matchMediaFn)
    return () => matchMedia?.removeEventListener('change', matchMediaFn)
  }, [matchMediaFn, matchMedia])

  return matchMedia
}

/**
 * Determines if the current viewport matches the provided screen
 * @param screenName Screen name based on settings/screens.ts
 * @returns boolean
 */
export function useScreenMatch(screenName: ScreenNames) {
  const matchMedia = useScreen(screenName)

  const [matches, setMatches] = useState<boolean | undefined>()

  useEffect(() => {
    if (matchMedia) {
      setMatches(matchMedia.matches)

      const onMediaChange = () => {
        setMatches(matchMedia.matches)
      }

      matchMedia?.addEventListener('change', onMediaChange)

      return () => matchMedia?.removeEventListener('change', onMediaChange)
    }
  }, [matchMedia])

  return matches
}

export type UseScreenMatchCallbackCallback<T = any> = (isMatch?: boolean) => T

/**
 *
 * @param screenName Which screen constraint to watch
 * @param callback Function to call when screen changes according to the supplied screenName constraint
 * @returns any
 */
export function useScreenMatchCallback(
  screenName: ScreenNames,
  callback: UseScreenMatchCallbackCallback,
) {
  const [returnValue, setReturnValue] = useState<any>()
  const isMatch = useScreenMatch(screenName)
  const [isMatchedState, setIsMatchedState] = useState(isMatch)

  useEffect(() => {
    if (
      typeof isMatch === 'boolean' &&
      typeof callback === 'function' &&
      isMatchedState !== isMatch
    ) {
      setReturnValue(callback(isMatch))
      setIsMatchedState(isMatch)
    }
  }, [isMatch, isMatchedState, callback])

  return returnValue
}
